import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { faCircleCheck, faCircleInfo, faLockKeyhole } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { MockupWindow, PlanMockup } from "@/pages/Public/components/ProductMockups";
import { trackCheckCtaClick } from "@/utils/analytics";

type Step = {
	step: string;
	title: string;
	description: string;
};

export default function QuickCheck() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const ts = (key: string) => t(`marketing.quickCheck.${key}`);

	const benefits = t("marketing.quickCheck.benefits", { returnObjects: true }) as string[];
	const steps = t("marketing.quickCheck.steps", { returnObjects: true }) as Step[];

	const handleStartCheck = (source: string) => {
		trackCheckCtaClick(source);
		navigate("/questionnaire/yes");
	};

	return (
		<div className="w-full flex flex-col items-center font-urbanist">
			{/* Hero */}
			<section className="w-full bg-surface py-14 sm:py-24 px-4 flex justify-center">
				<div className="max-w-[1420px] w-full flex flex-col lg:flex-row items-center justify-between gap-12">
					<div className="flex flex-col gap-8 max-w-[660px]">
						<h1 className="font-bold text-ink text-[36px] md:text-[48px] leading-[120%]">
							{ts("title")}
						</h1>
						<p className="font-medium text-subtle text-[18px] md:text-[20px] leading-[150%]">
							{ts("subtitle")}
						</p>
						<ul className="flex flex-col gap-3">
							{benefits?.map((benefit) => (
								<li key={benefit} className="flex items-start gap-3">
									<Icon className="w-5 h-5 mt-1 text-success-light" icon={faCircleCheck} />
									<span className="text-text text-[17px] font-medium leading-[150%]">
										{benefit}
									</span>
								</li>
							))}
						</ul>
						<button
							className="w-fit h-[52px] bg-accent hover:bg-accent-dark border border-transparent rounded-button px-[28px] py-[12px] text-[16px] font-bold text-white transition-colors"
							type="button"
							onClick={() => handleStartCheck("quickcheck_hero")}
						>
							{ts("ctaButton")}
						</button>
					</div>
					<div className="w-full max-w-[460px] md:max-w-[540px]">
						<MockupWindow>
							<PlanMockup />
						</MockupWindow>
					</div>
				</div>
			</section>

			{/* 3 steps */}
			<section className="w-full px-4 md:px-16">
				<div className="max-w-[1420px] mx-auto py-16 sm:py-24 flex flex-col gap-12 text-center">
					<h2 className="text-[32px] md:text-[40px] font-semibold leading-[120%] text-ink">
						{ts("stepsTitle")}
					</h2>
					<div className="grid grid-cols-1 md:grid-cols-3 gap-8">
						{steps?.map((step) => (
							<div
								key={step.step}
								className="flex flex-col items-center gap-4 bg-white border border-primary-gray-lightest rounded-2xl px-6 py-8"
							>
								<div className="rounded-full bg-gray-100 px-2.5 py-1 text-sm border border-primary-gray-lightest font-semibold">
									{step.step}
								</div>
								<h3 className="text-[20px] font-semibold text-ink">{step.title}</h3>
								<p className="text-[16px] leading-[150%] text-subtle">{step.description}</p>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* What the check is not + privacy */}
			<section className="w-full px-4 md:px-16 bg-surface">
				<div className="max-w-[1420px] mx-auto py-16 flex flex-col lg:flex-row gap-8 justify-center">
					<div className="flex gap-4 bg-white border border-primary-gray-lightest rounded-2xl p-6 max-w-[600px]">
						<Icon className="w-6 h-6 text-accent shrink-0 mt-1" icon={faCircleInfo} />
						<div className="flex flex-col gap-2 text-left">
							<h3 className="text-[18px] font-semibold text-ink">{ts("disclaimerTitle")}</h3>
							<p className="text-[16px] leading-[150%] text-subtle">{ts("disclaimerText")}</p>
						</div>
					</div>
					<div className="flex gap-4 bg-white border border-primary-gray-lightest rounded-2xl p-6 max-w-[600px]">
						<Icon className="w-6 h-6 text-primary-blue shrink-0 mt-1" icon={faLockKeyhole} />
						<div className="flex flex-col gap-2 text-left">
							<h3 className="text-[18px] font-semibold text-ink">{ts("privacyTitle")}</h3>
							<p className="text-[16px] leading-[150%] text-subtle">{ts("privacyText")}</p>
						</div>
					</div>
				</div>
			</section>

			{/* Single closing CTA */}
			<section className="w-full px-4 md:px-16">
				<div className="max-w-[1420px] mx-auto py-16 sm:py-24 flex flex-col items-center text-center gap-6">
					<h2 className="text-[32px] md:text-[40px] font-semibold leading-[120%] text-ink max-w-[820px]">
						{ts("finalCtaTitle")}
					</h2>
					<button
						className="px-12 py-3 rounded-button bg-accent hover:bg-accent-dark transition-colors text-white text-[16px] font-bold"
						type="button"
						onClick={() => handleStartCheck("quickcheck_final")}
					>
						{ts("ctaButton")}
					</button>
				</div>
			</section>
		</div>
	);
}
